import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { canAccessOfficeCentral } from "../utils/accessControl";
import "./HeaderPremium.css";

const READING_STORAGE_KEY = "zedro_academy_reading_mode";
const LANGUAGE_STORAGE_KEY = "zedro_academy_language";

const HEADER_COPY = {
  es: {
    menu: "Abrir menú",
    closeMenu: "Cerrar menú",
    account: "Cuenta",
    store: "Tienda",
    tagline: "Formación con raíz eterna",
    publicLinks: {
      home: "Inicio",
      methodology: "Metodología",
      news: "ZEDRO News",
      store: "Tienda",
    },
    reading: "Lectura",
    readingAria: "Ajuste de lectura",
    readingOptions: {
      normal: "Normal",
      medium: "Mediana",
      large: "Grande",
    },
    languageAria: "Seleccionar idioma",
  },
  fr: {
    menu: "Ouvrir le menu",
    closeMenu: "Fermer le menu",
    account: "Compte",
    store: "Boutique",
    tagline: "Formation aux racines éternelles",
    publicLinks: {
      home: "Accueil",
      methodology: "Méthodologie",
      news: "ZEDRO News",
      store: "Boutique",
    },
    reading: "Lecture",
    readingAria: "Réglage de lecture",
    readingOptions: {
      normal: "Normal",
      medium: "Moyenne",
      large: "Grande",
    },
    languageAria: "Sélectionner la langue",
  },
};

function getInitialLanguage() {
  if (typeof window === "undefined") return "es";

  const savedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return ["es", "fr"].includes(savedLanguage) ? savedLanguage : "es";
}

function trimTrailingSlash(pathname = "/") {
  const clean = String(pathname || "/").replace(/\/+$/, "");
  return clean || "/";
}

function getZedroNewsLanguageFromPath(pathname = "/") {
  const clean = trimTrailingSlash(pathname);

  if (clean === "/fr/zedro-news" || clean.startsWith("/fr/zedro-news/")) {
    return "fr";
  }

  if (clean === "/zedro-news" || clean.startsWith("/zedro-news/")) {
    return "es";
  }

  return "";
}

function getLocalizedZedroNewsPath(pathname = "/", nextLanguage = "es") {
  const clean = trimTrailingSlash(pathname);
  const spanishMatch = clean.match(/^\/zedro-news(?:\/(.+))?$/);
  const frenchMatch = clean.match(/^\/fr\/zedro-news(?:\/(.+))?$/);
  const match = spanishMatch || frenchMatch;

  if (!match) return "";

  const slug = match[1] ? `/${match[1]}` : "";
  return `${nextLanguage === "fr" ? "/fr/zedro-news" : "/zedro-news"}${slug}`;
}

const READING_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "medium", label: "Mediana" },
  { value: "large", label: "Grande" },
];

function applyReadingMode(mode) {
  document.body.classList.remove("zedro-reading-medium", "zedro-reading-large");

  if (mode === "medium") {
    document.body.classList.add("zedro-reading-medium");
  }

  if (mode === "large") {
    document.body.classList.add("zedro-reading-large");
  }
}

function IconMenu(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" {...props}>
      <path
        d="M4 7h16M4 12h16M4 17h16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconUser(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" {...props}>
      <path
        d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M4 20a8 8 0 0 1 16 0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconBag(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M6 9h12l-1.2 11H7.2L6 9Z" />
      <path d="M9 9V7a3 3 0 0 1 6 0v2" />
    </svg>
  );
}

export default function HeaderPremium({ onLogout, variant = "public" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, subscriptions = [] } = useAuth();

  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [readingOpen, setReadingOpen] = useState(false);
  const [readingMode, setReadingMode] = useState(() => {
    if (typeof window === "undefined") return "normal";

    const savedMode = window.localStorage.getItem(READING_STORAGE_KEY);
    return ["normal", "medium", "large"].includes(savedMode) ? savedMode : "normal";
  });
  const [language, setLanguage] = useState(getInitialLanguage);
  const [languageOpen, setLanguageOpen] = useState(false);

  const accountRef = useRef(null);

  const isAuthed = !!onLogout;
  const hasOfficeAccess = canAccessOfficeCentral(user);
  const showLanguageSwitch = !isAuthed && variant !== "admin";
  const headerCopy = HEADER_COPY[language] || HEADER_COPY.es;
  const currentReadingOptions = READING_OPTIONS.map((option) => ({
    ...option,
    label: headerCopy.readingOptions[option.value] || option.label,
  }));

  const activeSubscription = Array.isArray(subscriptions)
    ? subscriptions.find((s) => {
        if (s.status === "active") return true;

        if (s.status === "trialing") {
          return s.trial_end ? new Date(s.trial_end) > new Date() : false;
        }

        return false;
      })
    : null;

    function isCancelScheduled(subscription) {
    if (!subscription) return false;

    return (
      subscription.cancel_at_period_end === true ||
      subscription.cancel_at_period_end === "true" ||
      subscription.access_status === "trial_canceling" ||
      subscription.access_status === "active_canceling"
    );
  }

  function getSubscriptionStatusLabel(subscription) {
    if (!subscription) return "Sin membresía activa";

    if (isCancelScheduled(subscription) && subscription.status === "trialing") {
      return "Prueba cancelada";
    }

    if (isCancelScheduled(subscription)) {
      return "Cancelación programada";
    }

    if (subscription.status === "trialing") return "En prueba gratuita";
    if (subscription.status === "active") return "Activa";
    if (subscription.status === "canceled") return "Cancelada";
    if (subscription.status === "past_due") return "Pago pendiente";

    return "No disponible";
  }

  function getSubscriptionEndDate(subscription) {
    if (!subscription) return null;

    return (
      subscription.access_end ||
      subscription.current_period_end ||
      subscription.trial_end ||
      subscription.expires_at ||
      null
    );
  }

  function formatDate(dateValue) {
    if (!dateValue) return "—";

    try {
      return new Date(dateValue).toLocaleDateString("es-CA", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "—";
    }
  }

  const navLinks = useMemo(() => {
    if (!isAuthed) {
      return [
        { type: "title", label: "Navegación" },
        { type: "link", to: "/", label: headerCopy.publicLinks.home },
        { type: "link", to: "/metodologia", label: headerCopy.publicLinks.methodology },
        {
          type: "link",
          to: language === "fr" ? "/fr/zedro-news" : "/zedro-news",
          label: headerCopy.publicLinks.news,
        },
        { type: "link", to: "/store", label: headerCopy.publicLinks.store },
      ];
    }

    const links = [
      { type: "title", label: "Principal" },
      { type: "link", to: "/dashboard", label: "Mi recorrido" },
      { type: "link", to: "/core", label: "Cimientos ZEDRO" },
      { type: "link", to: "/bitacora", label: "Bitácora ZEDRO" },
      { type: "link", to: "/conexion", label: "Conexión ZEDRO" },
      { type: "link", to: "/dashboard/zedro-news", label: "ZEDRO News" },
      { type: "link", to: "/store", label: "Tienda" },
    ];

    if (hasOfficeAccess) {
      links.push({ type: "divider" });
      links.push({ type: "title", label: "Equipo ZEDRO" });
      links.push({ type: "link", to: "/admin", label: "Oficina Central" });
    }

    return links;
  }, [isAuthed, hasOfficeAccess, headerCopy, language]);

  function handleUserClick() {
    if (!isAuthed) {
      navigate("/login");
      return;
    }

    setAccountOpen((v) => !v);
  }

  function handleReadingModeChange(mode) {
    setReadingMode(mode);
  }

  function handleLanguageChange(nextLanguage) {
    if (!["es", "fr"].includes(nextLanguage)) return;

    const localizedNewsPath = getLocalizedZedroNewsPath(
      location.pathname,
      nextLanguage
    );

    setLanguage(nextLanguage);
    setLanguageOpen(false);

    if (localizedNewsPath && localizedNewsPath !== location.pathname) {
      navigate(localizedNewsPath);
    }
  }

  useEffect(() => {
    applyReadingMode(readingMode);
    window.localStorage.setItem(READING_STORAGE_KEY, readingMode);
  }, [readingMode]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    document.documentElement.lang = language;

    window.dispatchEvent(
      new CustomEvent("zedro:languagechange", {
        detail: { language },
      })
    );
  }, [language]);

  useEffect(() => {
    const routeLanguage = getZedroNewsLanguageFromPath(location.pathname);

    if (routeLanguage && routeLanguage !== language) {
      setLanguage(routeLanguage);
    }
  }, [location.pathname, language]);


  useEffect(() => {
    function handleClickOutside(e) {
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setAccountOpen(false);
      }
    }

    if (accountOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [accountOpen]);

  return (
    <header className="hp">
      <div className="hp-bar">
        {/* IZQUIERDA */}
        <div className="hp-left">
          <button
            type="button"
            className="hp-linkBtn hp-linkBtn--iconOnly"
            onClick={() => setMenuOpen(true)}
            aria-label={headerCopy.menu}
          >
            <IconMenu className="hp-ico" />
          </button>
        </div>

        {/* CENTRO */}
        <div
          className="hp-brand"
          onClick={() => navigate(variant === "admin" ? "/admin" : "/")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              navigate(variant === "admin" ? "/admin" : "/");
            }
          }}
        >
          <div className="hp-wordmark">Z E D R O</div>
          <div className="hp-tagline">
            {variant === "admin" ? "Oficina Central" : headerCopy.tagline}
          </div>
        </div>

        {/* DERECHA */}
        <div className="hp-right" ref={accountRef}>
          
          <button
            type="button"
            className="hp-icoBtn"
            onClick={handleUserClick}
            aria-label={headerCopy.account}
          >
            <IconUser className="hp-ico" />
          </button>

          {isAuthed && accountOpen && (
            <div className="hp-pop">
              <button
                type="button"
                className="hp-popItem"
                onClick={() => {
                  setAccountOpen(false);
                  navigate("/dashboard");
                }}
              >
                Mi cuenta
              </button>

              {hasOfficeAccess ? (
                <div className="hp-popSection">
                  <div className="hp-popLabel">Oficina Central</div>

                  <div className="hp-popMeta">Estado: Acceso administrativo</div>

                  <div className="hp-popMeta">
                    Rol: {user?.role === "STAFF" ? "Staff" : "Admin"}
                  </div>

                  <div className="hp-popMeta">Acceso: Permanente</div>
                </div>
              ) : (
                <div className="hp-popSection">
                  <div className="hp-popLabel">Mi membresía</div>

                  <div className="hp-popMeta">
                    Estado: {getSubscriptionStatusLabel(activeSubscription)}
                  </div>

                  <div className="hp-popMeta">
                    Desde:{" "}
                    {activeSubscription?.created_at
                      ? formatDate(activeSubscription.created_at)
                      : "—"}
                  </div>

                  <div className="hp-popMeta">
                    Hasta:{" "}
                    {getSubscriptionEndDate(activeSubscription)
                      ? formatDate(getSubscriptionEndDate(activeSubscription))
                      : "No disponible"}
                  </div>

                  {activeSubscription && (
                    <button
                      type="button"
                      className={
                        "hp-popItem" + (isCancelScheduled(activeSubscription) ? "" : " danger")
                      }
                      onClick={() => {
                        setAccountOpen(false);
                        navigate("/account/billing");
                      }}
                    >
                      {isCancelScheduled(activeSubscription)
                        ? "Reactivar membresía"
                        : "Cancelar suscripción"}
                    </button>
                  )}
                </div>
              )}

              <button
                type="button"
                className="hp-popItem danger"
                onClick={() => {
                  setAccountOpen(false);
                  onLogout?.();
                }}
              >
                Cerrar sesión
              </button>
            </div>
          )}

          {variant !== "admin" && (
            <button
              type="button"
              className="hp-icoBtn"
              onClick={() => navigate("/store")}
              aria-label={headerCopy.store}
            >
              <IconBag className="hp-ico" />
            </button>
          )}

          {showLanguageSwitch && (
            <div className="hp-langBox">
              <button
                type="button"
                className="hp-langCompactBtn"
                onClick={() => setLanguageOpen((value) => !value)}
                aria-label={headerCopy.languageAria}
                aria-expanded={languageOpen}
              >
                {language.toUpperCase()}
              </button>

              {languageOpen && (
               <div className="hp-langPop">
                  <button
                    type="button"
                    className={"hp-langPopItem" + (language === "es" ? " is-active" : "")}
                    onClick={() => handleLanguageChange("es")}
                  >
                    Español
                  </button>

                  <button
                    type="button"
                    className={"hp-langPopItem" + (language === "fr" ? " is-active" : "")}
                     onClick={() => handleLanguageChange("fr")}
                  >
                    Français
                  </button>
                </div>
              )}
            </div>
           )}
        </div>
      </div>

      {/* MENU */}
      {menuOpen && (
        <div className="hp-drawerWrap" role="dialog" aria-modal="true">
          <div
            className="hp-drawerBackdrop"
            onClick={() => setMenuOpen(false)}
          />

          <aside className="hp-drawer left">
            <div className="hp-drawerTop">
              <span className="hp-drawerTitle">
                {variant === "admin" ? "Oficina Central" : "ZEDRO Academy"}
              </span>

              <button
                type="button"
                className="hp-close"
                onClick={() => setMenuOpen(false)}
                aria-label={headerCopy.closeMenu}
              >
                ✕
              </button>
            </div>

            <div className="hp-drawerBody">
              <div
                className={"hp-readingBox" + (readingOpen ? " is-open" : "")}
                aria-label={headerCopy.readingAria}
              >
              <button
                type="button"
                className="hp-readingTrigger"
                onClick={() => setReadingOpen((v) => !v)}
                aria-expanded={readingOpen}
                aria-controls="hp-reading-options"
              >
                <div className="hp-readingTriggerLeft">
                  <span className="hp-readingIcon">Aa</span>
                  <span className="hp-readingLabel">{headerCopy.reading}</span>
                </div>

                <span className="hp-readingChevron">▾</span>
              </button>

              {readingOpen && (
                <div id="hp-reading-options" className="hp-readingOptions">
                  {currentReadingOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={
                        "hp-readingOption" +
                        (readingMode === option.value ? " is-active" : "")
                      }
                      onClick={() => handleReadingModeChange(option.value)}
                      aria-pressed={readingMode === option.value}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
              </div>

              <nav className="hp-drawerNav">
              {navLinks.map((item, idx) => {
                if (item.type === "divider") {
                  return <div key={`div-${idx}`} className="hp-divider" />;
                }

                if (item.type === "title") {
                  return (
                    <div key={`title-${idx}`} className="hp-drawerSectionTitle">
                      {item.label}
                    </div>
                  );
                }

                if (item.type === "action") {
                  return (
                    <button
                      key={`act-${idx}`}
                      type="button"
                      className="hp-drawerBtn"
                      onClick={() => {
                        setMenuOpen(false);
                        item.onClick?.();
                      }}
                    >
                      {item.label}
                    </button>
                  );
                }

                if (item.type === "disabled") {
                  return (
                    <button
                      key={`dis-${idx}`}
                      type="button"
                      className="hp-drawerBtn hp-drawerBtn--disabled"
                      disabled
                    >
                      {item.label}
                    </button>
                  );
                }

                return (
                  <NavLink
                    key={`lnk-${item.to}-${idx}`}
                    to={item.to}
                    className={({ isActive }) =>
                      "hp-drawerLink" + (isActive ? " is-active" : "")
                    }
                    onClick={() => setMenuOpen(false)}
                  >
                    {item.label}
                  </NavLink>
                );
              })}
              </nav>
            </div>

            {isAuthed && (
              <div className="hp-drawerFooter">
                <button
                  type="button"
                  className="hp-drawerBtn hp-drawerBtn--logout"
                  onClick={() => {
                    setMenuOpen(false);
                    onLogout?.();
                  }}
                >
                  Cerrar sesión
                </button>
              </div>
            )}
          </aside>
        </div>
      )}
    </header>
  );
}